import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import * as ReactTooltip from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { NotificationProvider } from './contexts/NotificationContext';
import PrivateRoute from './components/PrivateRoute';
import Navbar from './components/Navbar';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import BackToTop from './components/BackToTop';
import MobileBottomNav from './components/MobileBottomNav';
import BetaFeedbackWidget from './components/BetaFeedbackWidget';
import WelcomeBanner from './components/WelcomeBanner';
import KeyboardShortcutsModal from './components/KeyboardShortcutsModal';
import TutorialOverlay from './components/TutorialOverlay';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import './App.css';
import './styles/holographic.css';

const Tooltip = ReactTooltip?.Tooltip ?? (() => null);

// Initialize Stripe
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Debug: Log if key is missing (only in development)
if (process.env.NODE_ENV === 'development' && !stripePublishableKey) {
  console.warn('⚠️ REACT_APP_STRIPE_PUBLISHABLE_KEY is not set in .env file');
}

// Only initialize Stripe if we have a valid key
const stripePromise = stripePublishableKey && stripePublishableKey.trim() !== '' 
  ? loadStripe(stripePublishableKey.trim()) 
  : null;

// Lazy load pages for code splitting
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Feed = lazy(() => import('./pages/Feed'));
const CreatePost = lazy(() => import('./pages/CreatePost'));
const Profile = lazy(() => import('./pages/Profile'));
const CardTagForm = lazy(() => import('./pages/CardTagForm'));
const ConfirmPasswordChange = lazy(() => import('./pages/ConfirmPasswordChange'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const FollowersList = lazy(() => import('./pages/FollowersList'));
const FollowingList = lazy(() => import('./pages/FollowingList'));
const EditProfile = lazy(() => import('./pages/EditProfile'));
const SavedSearches = lazy(() => import('./pages/SavedSearches'));
const MyCards = lazy(() => import('./pages/MyCards'));
const UserSearch = lazy(() => import('./pages/UserSearch'));
const Notifications = lazy(() => import('./pages/Notifications'));
const CardDetail = lazy(() => import('./pages/CardDetail'));
const Messages = lazy(() => import('./pages/Messages'));
const Settings = lazy(() => import('./pages/Settings'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SavedPosts = lazy(() => import('./pages/SavedPosts'));
const Trades = lazy(() => import('./pages/Trades'));
const TradeDetail = lazy(() => import('./pages/TradeDetail'));
const CreateTrade = lazy(() => import('./pages/CreateTrade'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const Addresses = lazy(() => import('./pages/Addresses'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CreateListing = lazy(() => import('./pages/CreateListing'));
const ListingDetail = lazy(() => import('./pages/ListingDetail'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const Groups = lazy(() => import('./pages/Groups'));
const GroupDetail = lazy(() => import('./pages/GroupDetail'));
const CreateGroup = lazy(() => import('./pages/CreateGroup'));
const GroupSettings = lazy(() => import('./pages/GroupSettings'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));

// Loading fallback component
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <LoadingSpinner size="lg" message="Loading page..." />
  </div>
);

// AppContent component to use hooks inside Router
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { isAuthenticated } = useAuth();
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);

  // Scroll to top and cleanup on route changes
  useEffect(() => {
    // Back/forward into the marketplace restores its own scroll position.
    if (!(navigationType === 'POP' && location.pathname === '/marketplace')) {
      window.scrollTo(0, 0);
    }
    // Reset body overflow in case a modal left it locked
    document.body.style.overflow = '';
    
    // Remove any lingering modal overlays (safety check)
    const modalOverlays = document.querySelectorAll('.payment-modal-overlay, .modal-overlay, .shipping-modal-overlay');
    modalOverlays.forEach(overlay => {
      // Only remove if it's not supposed to be visible (no isOpen check, just cleanup)
      if (overlay.style.display !== 'none') {
        // This is a safety measure - components should handle their own cleanup
        // But we'll ensure body scroll is always restored
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- navigationType is read for the current change only
  }, [location.pathname]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    '/': (e) => {
      if (isAuthenticated && location.pathname !== '/search') {
        e.preventDefault();
        navigate('/search');
        // Focus search input if it exists
        setTimeout(() => {
          const searchInput = document.querySelector('input[type="search"], input[placeholder*="Search"]');
          if (searchInput) {
            searchInput.focus();
          }
        }, 100);
      }
    },
    'n': (e) => {
      if (isAuthenticated && location.pathname !== '/create-post') {
        e.preventDefault();
        navigate('/create-post');
      }
    },
    '?': (e) => {
      if (isAuthenticated) {
        e.preventDefault();
        setShowShortcutsModal(true);
      }
    },
  }, [isAuthenticated, location.pathname, navigate]);

  const authPages = ['/login', '/register', '/forgot-password', '/reset-password', '/confirm-password-change'];
  const isAuthPage = authPages.some(p => location.pathname.startsWith(p));

  return (
    <>
      {!isAuthPage && <Navbar />}
      {isAuthenticated && !isAuthPage && <BetaFeedbackWidget />}
      {isAuthenticated && location.pathname === '/feed' && <WelcomeBanner />}
      <Tooltip
        id="main-tooltip"
        place="top"
        className="custom-tooltip"
        delayShow={200}
        style={{ zIndex: 99999, fontSize: '12px', padding: '5px 10px', borderRadius: '6px' }}
      />
      <TutorialOverlay />
      <main id="main-content" role="main">
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
                borderRadius: '12px',
                padding: '16px 20px',
                fontSize: '14px',
                fontFamily: "'Inter', sans-serif",
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                maxWidth: '400px',
              },
              success: {
                iconTheme: {
                  primary: '#4ade80',
                  secondary: '#fff',
                },
                style: {
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
                style: {
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                },
              },
            }}
          />
              {/* Screen reader announcements */}
              <div id="sr-announcements" aria-live="polite" aria-atomic="true" className="sr-only"></div>
              <ErrorBoundary
                title="Application Error"
                message="The application encountered an unexpected error. Please try refreshing the page."
                showRetry={true}
                showHome={true}
              >
                <Suspense fallback={<PageLoader />}>
                  <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/confirm-password-change" element={<ConfirmPasswordChange />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route
                path="/post/:postId"
                element={
                  <PrivateRoute>
                    <PostDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/feed"
                element={
                  <PrivateRoute>
                    <Feed />
                  </PrivateRoute>
                }
              />
              <Route
                path="/create-post"
                element={
                  <PrivateRoute>
                    <CreatePost />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile/edit"
                element={
                  <PrivateRoute>
                    <EditProfile />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile/:userId"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile/followers"
                element={
                  <PrivateRoute>
                    <FollowersList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile/following"
                element={
                  <PrivateRoute>
                    <FollowingList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile/:userId/followers"
                element={
                  <PrivateRoute>
                    <FollowersList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile/:userId/following"
                element={
                  <PrivateRoute>
                    <FollowingList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/card-tag"
                element={
                  <PrivateRoute>
                    <CardTagForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/searches"
                element={
                  <PrivateRoute>
                    <SavedSearches />
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-cards"
                element={
                  <PrivateRoute>
                    <MyCards />
                  </PrivateRoute>
                }
              />
              <Route
                path="/search"
                element={
                  <PrivateRoute>
                    <UserSearch />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <PrivateRoute>
                    <Notifications />
                  </PrivateRoute>
                }
              />
              <Route
                path="/card/:cardId"
                element={
                  <PrivateRoute>
                    <CardDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/messages"
                element={
                  <PrivateRoute>
                    <Messages />
                  </PrivateRoute>
                }
              />
              <Route
                path="/messages/:conversationId"
                element={
                  <PrivateRoute>
                    <Messages />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <Settings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/users/addresses"
                element={
                  <PrivateRoute>
                    <Addresses />
                  </PrivateRoute>
                }
              />
              <Route
                path="/saved-posts"
                element={
                  <PrivateRoute>
                    <SavedPosts />
                  </PrivateRoute>
                }
              />
              {/* Trades routes commented out - feature temporarily disabled */}
              {/* <Route
                path="/trades"
                element={
                  <PrivateRoute>
                    <Trades />
                  </PrivateRoute>
                }
              />
              <Route
                path="/trades/new"
                element={
                  <PrivateRoute>
                    <CreateTrade />
                  </PrivateRoute>
                }
              />
              <Route
                path="/trades/:tradeId"
                element={
                  <PrivateRoute>
                    <TradeDetail />
                  </PrivateRoute>
                }
              /> */}
              <Route
                path="/marketplace"
                element={
                  <PrivateRoute>
                    <Marketplace />
                  </PrivateRoute>
                }
              />
              <Route
                path="/cart"
                element={
                  <PrivateRoute>
                    <CartPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/marketplace/create"
                element={
                  <PrivateRoute>
                    <CreateListing />
                  </PrivateRoute>
                }
              />
              <Route
                path="/marketplace/:listingId/edit"
                element={
                  <PrivateRoute>
                    <CreateListing />
                  </PrivateRoute>
                }
              />
              <Route
                path="/marketplace/:listingId"
                element={
                  <PrivateRoute>
                    <ListingDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/marketplace/:listingId/edit"
                element={
                  <PrivateRoute>
                    <CreateListing />
                  </PrivateRoute>
                }
              />
              <Route
                path="/marketplace/orders/:orderId"
                element={
                  <PrivateRoute>
                    <OrderDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/groups"
                element={
                  <PrivateRoute>
                    <Groups />
                  </PrivateRoute>
                }
              />
              <Route
                path="/groups/new"
                element={
                  <PrivateRoute>
                    <CreateGroup />
                  </PrivateRoute>
                }
              />
                    <Route
                      path="/groups/:groupId"
                      element={
                        <PrivateRoute>
                          <GroupDetail />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/groups/:groupId/settings"
                      element={
                        <PrivateRoute>
                          <GroupSettings />
                        </PrivateRoute>
                      }
                    />
                  <Route
                    path="/"
                    element={isAuthenticated ? <Navigate to="/feed" replace /> : <Landing />}
                  />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
          </main>
          {!location.pathname.startsWith('/messages') && <BackToTop />}
          {isAuthenticated && !isAuthPage && !location.pathname.startsWith('/messages') && <MobileBottomNav />}
          <KeyboardShortcutsModal 
            isOpen={showShortcutsModal} 
            onClose={() => setShowShortcutsModal(false)} 
          />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        {stripePromise ? (
          <Elements stripe={stripePromise} options={{ appearance: { theme: 'stripe' } }}>
            <Router>
              <div className="App">
                <AppContent />
              </div>
            </Router>
          </Elements>
        ) : (
          <Router>
            <div className="App">
              <AppContent />
            </div>
          </Router>
        )}
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;

